import classnames from 'classnames';

import { type CSSProperties, type ChangeEventHandler, useCallback, useMemo } from 'react';
import { Icon } from '../icon';
import { computeFont } from '../utils/fonts';
import * as styles from './Signup.css';
import type { SignupElementProps } from './Signup.types';
import AdditionalFields from './components/AdditionalFields';
import GDPRCompliance from './components/GDPRCompliance';
import SignupInput from './components/SignupInput';

const MOBILE_CLASSES = {
  vertical: 'flex-col',
  horizontal: 'flex-row',
};

const DESKTOP_CLASSES = {
  vertical: 'sm:flex-col',
  horizontal: 'sm:flex-row',
};

const orientationClass = (orientation: 'vertical' | 'horizontal', mobileOrientation: 'vertical' | 'horizontal') => {
  const mobileClass = MOBILE_CLASSES[mobileOrientation];
  const desktopClass = DESKTOP_CLASSES[orientation];

  return classnames(mobileClass, desktopClass);
};

const getCheckboxRadius = (borderRadius: string): string => {
  if (!borderRadius) return '6px';

  try {
    const firstValue = borderRadius.split(' ')[0] || '';

    // Extract numeric value from the first part (e.g., "42px")
    const match = firstValue.match(/^(\d+)/);
    const borderRadiusValue = match ? Number.parseInt(match[1], 10) : Number.NaN;

    if (Number.isNaN(borderRadiusValue)) return '6px';
    const resolvedBorderRadius = Math.min(borderRadiusValue, 6);
    return `${resolvedBorderRadius}px`;
  } catch (error) {
    console.error('Error parsing border radius:', error);
    return '6px';
  }
};

export function SignupElement(props: SignupElementProps) {
  const { element, children, attributes, className, isSubmitting, isLive, onEmailChange, gdprTranslations } = props;

  const {
    height,
    width,
    mobile_hide,
    desktop_hide,
    customAttributes,
    borderWidth,
    borderRadius,
    borderColor,
    borderStyle,
    padding,
    backgroundColor,
    gap,
    inputBorderWidth,
    inputBorderRadius,
    inputBorderColor,
    inputBackgroundColor,
    inputBorderStyle,
    inputRadioCheckboxAccentColor,
    inputTextColor,
    inputLabelColor,
    inputShowLabel,
    inputFontWeight,
    inputFontSize,
    inputEmailLabel,
    mobileInputFontSize,
    mobileBorderRadius,
    inputPlaceholderColor,
    inputRadioCheckboxBackgroundColor,
    inputPlaceholder,
    buttonBorderWidth,
    buttonBorderRadius,
    buttonBorderColor,
    buttonBorderStyle,
    buttonBackgroundColor,
    buttonTextColor,
    buttonText,
    buttonFontWeight,
    buttonFontSize,
    mobileButtonFontSize,
    mobileButtonBorderRadius,
    buttonSubmittingText,
    icon,
    iconColor,
    gdprTextColor,
    gdprCheckboxColor,
    orientation,
    mobileOrientation,
    inputFontFamily,
    buttonFontFamily,
    buttonIconSide,
    buttonIcon,
    buttonIconColor,
    data,
  } = element.attrs || {};

  const hasTermsOfService = isLive ? props.hasTermsOfService : element.attrs.hasTermsOfService;
  const hasCustomTermsOfService = isLive ? props.hasCustomTermsOfService : element.attrs.hasCustomTermsOfService;
  const hasAdditionalFields = data && data?.preferences?.length > 0;
  const hasBorder = inputBorderStyle !== 'none';

  const inputStyle = {
    borderColor: inputBorderColor,
    borderWidth: inputBorderWidth,
    borderRadius: inputBorderRadius,
    borderStyle: inputBorderStyle,
    height: height || '50px',
    color: inputTextColor,
    '--placeholder-color': inputPlaceholderColor,
    '--input-icon-color': iconColor,
  } as CSSProperties;

  const computedCheckboxRadius = useMemo(() => {
    return getCheckboxRadius(
      typeof inputBorderRadius === 'number' ? `${inputBorderRadius}px` : inputBorderRadius || '6px',
    );
  }, [inputBorderRadius]);

  const handleEmailChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    if (e.currentTarget.value) {
      onEmailChange?.(e.currentTarget.value);
    }
  }, []);

  return (
    <div
      {...customAttributes}
      className={classnames(
        className,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
        },
        styles.wrapper,
        customAttributes?.className,
        'flex flex-col gap-2',
        !isLive && 'pointer-events-none',
      )}
      style={
        {
          width: '100%',
          maxWidth: width,
          '--gap': gap,
          '--input-background-color': inputBackgroundColor,
          '--input-border-color': hasBorder ? inputBorderColor : 'transparent',
          '--input-checkbox-radius': computedCheckboxRadius,
          '--input-text-color': inputTextColor,
          '--input-border-width': inputBorderWidth,
          '--input-border-radius': inputBorderRadius,
          '--input-radio-checkbox-accent-color': inputRadioCheckboxAccentColor,
          '--input-radio-checkbox-background-color': inputRadioCheckboxBackgroundColor,
          '--input-label-color': inputLabelColor,
          '--input-icon-color': iconColor,
          '--gdpr-text-color': gdprTextColor,
          '--gdpr-checkbox-color': gdprCheckboxColor,
          '--input-font-family': computeFont(inputFontFamily),
          '--button-font-family': computeFont(buttonFontFamily),
          '--button-icon-side': buttonIconSide,
          '--button-icon': buttonIcon,
          '--button-icon-color': buttonIconColor,
          '--input-font-weight': inputFontWeight,
          '--input-font-size': inputFontSize,
          '--mobile-input-font-size': mobileInputFontSize || inputFontSize,
          '--button-font-weight': buttonFontWeight,
          '--button-font-size': buttonFontSize,
        } as CSSProperties
      }
      {...attributes}
    >
      <div
        className={classnames(
          styles.signupElement,
          'flex',
          hasAdditionalFields
            ? 'flex-col'
            : `${orientationClass(orientation || 'horizontal', mobileOrientation || 'vertical')}`,
          {
            'pointer-events-none': isSubmitting,
          },
        )}
        style={
          {
            borderColor: borderColor,
            borderWidth: borderWidth,
            borderStyle: borderStyle,
            padding: padding,
            backgroundColor: backgroundColor,
            width: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
            gap,
            '--border-radius': borderRadius,
            '--mobile-border-radius': mobileBorderRadius || borderRadius,
          } as CSSProperties
        }
        {...attributes}
      >
        {hasAdditionalFields && inputShowLabel ? (
          <div className="flex flex-col gap-1 w-full">
            <label className={classnames(styles.inputLabelStyle, 'text-left w-full')} htmlFor={element.attrs.id}>
              {inputEmailLabel || 'Email'}
              <span>*</span>
            </label>
            <SignupInput
              icon={icon}
              inputPlaceholder={inputPlaceholder || 'Email'}
              handleEmailChange={handleEmailChange}
              inputStyle={inputStyle}
            />
          </div>
        ) : (
          <SignupInput
            icon={icon}
            inputPlaceholder={inputPlaceholder || 'Email'}
            handleEmailChange={handleEmailChange}
            inputStyle={inputStyle}
          />
        )}

        {hasAdditionalFields && (
          <AdditionalFields data={data?.preferences || []} style={inputStyle} isLabelVisible={inputShowLabel} />
        )}
        <button
          type="submit"
          className={classnames(
            styles.button,
            isSubmitting && 'opacity-50',
            'flex items-center justify-center whitespace-nowrap',
          )}
          style={
            {
              borderColor: buttonBorderColor,
              borderWidth: buttonBorderWidth,
              borderStyle: buttonBorderStyle,
              backgroundColor: buttonBackgroundColor,
              color: buttonTextColor,
              boxSizing: 'border-box',
              height: height || '50px',
              padding: '0 16px 0 16px',
              '--mobile-button-font-size': mobileButtonFontSize || buttonFontSize,
              '--button-border-radius': buttonBorderRadius,
              '--mobile-button-border-radius': mobileButtonBorderRadius || buttonBorderRadius,
            } as CSSProperties
          }
        >
          {buttonIconSide === 'left' && buttonIcon && (
            <div className={classnames(styles.buttonIcon, 'mr-2')}>
              {buttonIcon.type === 'icon' && <Icon type={buttonIcon.iconType} name={buttonIcon.iconName} size={20} />}
              {buttonIcon.type === 'emoji' && <span>{buttonIcon.content}</span>}
            </div>
          )}
          {isSubmitting ? buttonSubmittingText : buttonText}
          {buttonIconSide === 'right' && buttonIcon && (
            <div className={classnames(styles.buttonIcon, 'ml-2')}>
              {buttonIcon.type === 'icon' && <Icon type={buttonIcon.iconType} name={buttonIcon.iconName} size={20} />}
              {buttonIcon.type === 'emoji' && <span>{buttonIcon.content}</span>}
            </div>
          )}
        </button>
      </div>

      {hasTermsOfService && (
        <GDPRCompliance
          hasCustomTermsOfService={hasCustomTermsOfService}
          defaultTOSConsentValue={false}
          gdprTranslations={gdprTranslations}
        />
      )}
      {children}
    </div>
  );
}
